/**
 * Shared, reusable UI primitives for PoseRef panels.
 *
 * Every panel should compose its layout from these building blocks so the
 * application looks and feels consistent as it grows.
 */

import {
  ChevronDown,
  ChevronRight,
  Minus,
  Pencil,
  Pipette,
  Plus,
  Search,
  X,
} from "lucide-react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../app/store.ts";
import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import { useDeferredUndo } from "./hooks/useDeferredUndo.ts";
import {
  EyeClosedIcon,
  EyeOpenIcon,
  StarFilledIcon,
  StarIcon,
} from "./Icons.tsx";
import { Popover } from "./Popover.tsx";
import { SidebarCard } from "./SidebarCard.tsx";
import { Slider } from "./Slider.tsx";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_ACTIVE_BG_SUBTLE,
  COLOR_BORDER,
  COLOR_BORDER_STRONG,
  COLOR_DANGER,
  COLOR_DANGER_BG,
  COLOR_GOLD,
  COLOR_MOD_ARRAY,
  COLOR_MOD_BEVEL,
  COLOR_MOD_BOOLEAN,
  COLOR_MOD_CURVE,
  COLOR_MOD_FOLLOW,
  COLOR_MOD_INSTANCE,
  COLOR_MOD_NOISE,
  COLOR_MOD_SHRINKWRAP,
  COLOR_MOD_SKIN,
  COLOR_MOD_SMOOTH,
  COLOR_MOD_SOLIDIFY,
  COLOR_MOD_SUBSURF,
  COLOR_SURFACE,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_MUTED,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_BODY,
  FONT_SIZE_PANEL_TITLE,
  FONT_SIZE_SECTION,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_CONTROL,
  RADIUS_DOT,
  RADIUS_SM,
  RADIUS_XS,
  SPACE_MD,
  SPACE_SM,
  useIsMobile,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Capture-phase keyboard handler for modals and menus.
 *
 * While `open` is true, intercepts Escape, Enter, and blocks non-modifier
 * keys from leaking to the global dispatcher (e.g. G/R/S shortcuts).
 * Cmd/Ctrl combos are always allowed through.
 */
export function useModalKeyboard(
  open: boolean,
  handlers: { onEscape?: () => void; onEnter?: () => void },
): void {
  // Keep handler refs stable so the effect doesn't re-bind on every render
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        ref.current.onEscape?.();
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        ref.current.onEnter?.();
      } else if (!(e.metaKey || e.ctrlKey)) {
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open]);
}

/**
 * Transient boolean feedback — returns `[active, fire]`.
 * Calling `fire()` sets `active` to true for `durationMs` (default 1200),
 * then resets. Handles cleanup on unmount and re-fire before expiry.
 */
export function useTransientFeedback(durationMs = 1200): [boolean, () => void] {
  const [active, setActive] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);
  const fire = useCallback(() => {
    setActive(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setActive(false), durationMs);
  }, [durationMs]);
  useEffect(() => () => window.clearTimeout(timerRef.current), []);
  return [active, fire];
}

/**
 * Close a modal/menu when the user clicks outside the referenced element.
 * Attaches a capture-phase pointerdown listener while `open` is true.
 */
export function useOutsideClick(
  ref: RefObject<HTMLElement | null>,
  onClose: () => void,
  open = true,
): void {
  useEffect(() => {
    if (!open) return;
    const handler = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    window.addEventListener("pointerdown", handler, true);
    return () => window.removeEventListener("pointerdown", handler, true);
  }, [open, onClose, ref]);
}

/**
 * Blender-style inline rename hook — owns every subtlety of the editing
 * experience in one place: text auto-select on focus, commit on Enter /
 * blur, cancel on Escape, rejection of empty / whitespace-only names, and
 * stopping key/click propagation so the rename doesn't interact with
 * ambient keyboard shortcuts or the parent row's click handler.
 *
 * Consumers render whatever display element they want (plain `<span>`,
 * themed label, etc.), wire it to `startRenaming` on double-click, and
 * swap to `<input {...inputProps} />` when `isRenaming` is true. No
 * styling is imposed beyond the `NAME` tokens the caller already passes
 * in — the hook only handles behavior.
 */
export function useInlineRename(
  value: string,
  onCommit: (next: string) => void,
): {
  isRenaming: boolean;
  startRenaming: (e?: { stopPropagation?: () => void }) => void;
  inputProps: {
    ref: RefObject<HTMLInputElement | null>;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: () => void;
    onClick: (e: React.MouseEvent) => void;
    onKeyDown: (e: React.KeyboardEvent) => void;
  };
} {
  const [isRenaming, setIsRenaming] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep draft in sync when value changes externally while not editing.
  useEffect(() => {
    if (!isRenaming) setDraft(value);
  }, [value, isRenaming]);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const commit = useCallback(() => {
    setIsRenaming(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onCommit(trimmed);
    } else {
      setDraft(value);
    }
  }, [draft, value, onCommit]);

  const startRenaming = useCallback(
    (e?: { stopPropagation?: () => void }) => {
      e?.stopPropagation?.();
      setDraft(value);
      setIsRenaming(true);
    },
    [value],
  );

  return {
    isRenaming,
    startRenaming,
    inputProps: {
      ref: inputRef,
      value: draft,
      onChange: (e) => setDraft(e.target.value),
      onBlur: commit,
      // Stop click from bubbling to parent row selection handlers.
      onClick: (e) => e.stopPropagation(),
      onKeyDown: (e) => {
        // Let Cmd/Ctrl shortcuts through; stop plain keys so the app's
        // global shortcut dispatcher doesn't see them while editing.
        if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
        if (e.key === "Enter") commit();
        if (e.key === "Escape") {
          setDraft(value);
          setIsRenaming(false);
        }
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Panel shells — consistent positioning & glass treatment
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Panel header — top-level title inside a glass card
// ---------------------------------------------------------------------------

export function PanelHeader({
  icon,
  children,
}: {
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: FONT_SIZE_PANEL_TITLE,
        fontWeight: 500,
        color: COLOR_TEXT_MUTED,
        marginBottom: 12,
      }}
    >
      {icon && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: COLOR_TEXT_FAINT,
          }}
        >
          {icon}
        </span>
      )}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section label — small caps label for groups within a panel
// ---------------------------------------------------------------------------

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: FONT_SIZE_SECTION,
        color: COLOR_TEXT_FAINT,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubsectionLabel — smaller label for groups within a section (e.g. Position,
// Rotation, Scale within the Transform section)
// ---------------------------------------------------------------------------

export function SubsectionLabel({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontSize: FONT_SIZE_SECTION,
        color: COLOR_TEXT_FAINT,
        textTransform: "uppercase",
        letterSpacing: 0.8,
      }}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// EyeToggle — label + eye icon toggle used for visibility-style booleans
// ---------------------------------------------------------------------------

export function EyeToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 0",
        cursor: "pointer",
      }}
    >
      <span style={{ fontSize: FONT_SIZE_SMALL, color: COLOR_TEXT_SECONDARY }}>
        {label}
      </span>
      <span
        style={{
          color: checked ? COLOR_ACCENT : COLOR_TEXT_DISABLED,
          display: "flex",
          alignItems: "center",
        }}
      >
        {checked ? <EyeOpenIcon /> : <EyeClosedIcon />}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SearchField — icon + input combo used across panels
// ---------------------------------------------------------------------------

export function SearchField({
  value,
  onChange,
  placeholder = "Search…",
  inputRef,
  onKeyDown,
  clearable,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Show a clear button when the field has a value. */
  clearable?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: RADIUS_CONTROL,
        background: COLOR_SURFACE,
        border: `0.5px solid ${COLOR_BORDER_STRONG}`,
      }}
    >
      <Search size={13} style={{ color: COLOR_TEXT_FAINT, flexShrink: 0 }} />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={{
          flex: 1,
          border: "none",
          background: "transparent",
          color: COLOR_TEXT,
          fontSize: FONT_SIZE_BODY,
          outline: "none",
          fontFamily: "inherit",
          minWidth: 0,
        }}
      />
      {clearable && value && (
        <button
          onClick={() => onChange("")}
          style={{
            background: "none",
            border: "none",
            color: COLOR_TEXT_DIM,
            cursor: "pointer",
            padding: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TabBar — horizontal tab switcher (pill or underline variant)
// ---------------------------------------------------------------------------

export function TabBar<T extends string>({
  tabs,
  active,
  onChange,
  variant = "pill",
}: {
  tabs: { id: T; label: string; icon?: ReactNode }[];
  active: T;
  onChange: (id: T) => void;
  /**
   * "pill" — filled highlight for compact panels (default).
   * "underline" — bottom-border accent line for modal / full-width layouts.
   */
  variant?: "pill" | "underline";
}) {
  if (variant === "underline") {
    return (
      <div
        style={{
          display: "flex",
          gap: 0,
          borderBottom: `0.5px solid ${COLOR_BORDER}`,
        }}
      >
        {tabs.map((t) => {
          const isActive = t.id === active;
          return (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "10px 16px",
                border: "none",
                background: "transparent",
                color: isActive ? COLOR_ACCENT : COLOR_TEXT_DIM,
                fontSize: FONT_SIZE_BODY,
                fontWeight: isActive ? 600 : 400,
                cursor: "pointer",
                borderBottom: isActive
                  ? `2px solid ${COLOR_ACCENT}`
                  : "2px solid transparent",
                transition: "color 0.15s",
              }}
            >
              {t.icon}
              {t.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        gap: 2,
        padding: "4px 0",
        background: COLOR_SURFACE,
        borderRadius: RADIUS_SM,
      }}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            flex: 1,
            padding: "6px 0",
            border: "none",
            background: active === t.id ? COLOR_ACTIVE_BG : "transparent",
            color: active === t.id ? COLOR_TEXT : COLOR_TEXT_FAINT,
            fontSize: FONT_SIZE_SMALL,
            cursor: "pointer",
            borderRadius: RADIUS_SM,
            fontWeight: 500,
            transition: "background 100ms, color 100ms",
          }}
        >
          {t.icon}
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FoldoutSection — lightweight collapsible section header used inside panels.
//
// This is the primitive. CollapsiblePanel (below) wraps it in a glass card
// for top-level sidebar panels; use FoldoutSection directly for in-panel
// groupings (e.g. generator param sections).
//
// Collapsed state is persisted in localStorage alongside CollapsiblePanel
// state — both use the same key so the Set is shared.
// ---------------------------------------------------------------------------

const COLLAPSE_STORAGE_KEY = "poseref-panel-collapsed";

function readCollapsedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function writeCollapsedSet(s: Set<string>) {
  localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify([...s]));
}

export function FoldoutSection({
  id,
  label,
  icon,
  summary,
  defaultCollapsed = false,
  isPanelHeader = false,
  children,
}: {
  /** Stable key for persisting collapsed state in localStorage. */
  id: string;
  /** Section heading text (rendered uppercase). */
  label: string;
  /** Optional icon displayed before the label. */
  icon?: ReactNode;
  /** Compact summary shown to the right of the label when collapsed. */
  summary?: ReactNode;
  /** Starting state on first visit (before any user interaction). */
  defaultCollapsed?: boolean;
  /**
   * When true, marks the header row with `data-panel-header` so the
   * InspectorPanel drag-to-reorder logic can identify it as a drag handle.
   * Set only by CollapsiblePanel — not for in-panel foldout sections.
   */
  isPanelHeader?: boolean;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(() => {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (raw !== null) return readCollapsedSet().has(id);
    return defaultCollapsed;
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      const s = readCollapsedSet();
      if (next) s.add(id);
      else s.delete(id);
      writeCollapsedSet(s);
      return next;
    });
  }, [id]);

  return (
    <div>
      <div
        onClick={toggle}
        {...(isPanelHeader && { "data-panel-header": "" })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: COLOR_TEXT_FAINT,
            flexShrink: 0,
          }}
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        </span>
        {icon && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              color: COLOR_TEXT_FAINT,
              flexShrink: 0,
            }}
          >
            {icon}
          </span>
        )}
        <span
          style={{
            fontSize: FONT_SIZE_SECTION,
            color: COLOR_TEXT_FAINT,
            textTransform: "uppercase",
            letterSpacing: 0.8,
            flex: 1,
          }}
        >
          {label}
        </span>
        {collapsed && summary && (
          <span
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_DIM,
              fontVariantNumeric: "tabular-nums",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 140,
            }}
          >
            {summary}
          </span>
        )}
      </div>
      {!collapsed && <div style={{ marginTop: 6 }}>{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CollapsibleSectionHeader — fully controlled header row for list-of-sections
// patterns (e.g. a modifier stack). Unlike `FoldoutSection`, this primitive
// does *not* own its collapse state — the parent passes `isCollapsed` /
// `onToggle`, which is what list-driven panels need so the collapse set can
// live in the store or be keyed off a stable list index.
//
// Layout: [accentDot?] [chevron] [leadingIcon?] [title ...] [actions?]
//
// Clicks anywhere on the row fire `onToggle`. Clicks inside `actions` are
// stopped from bubbling so per-item buttons (reorder, delete, visibility)
// don't also toggle collapse.
// ---------------------------------------------------------------------------

export interface CollapsibleSectionHeaderProps {
  title: ReactNode;
  isCollapsed: boolean;
  onToggle: () => void;
  /** Small colored accent dot rendered before the chevron. Omit to hide. */
  accentColor?: string;
  /** Optional leading icon (e.g. a modifier-kind glyph). */
  leadingIcon?: ReactNode;
  /** Right-aligned controls (drag handle, delete, visibility). Clicks inside do NOT fire onToggle. */
  actions?: ReactNode;
  /** Adds an "active" highlight — selected / editing state. */
  active?: boolean;
  /** Inline style overrides. */
  style?: CSSProperties;
}

export function CollapsibleSectionHeader({
  title,
  isCollapsed,
  onToggle,
  accentColor,
  leadingIcon,
  actions,
  active = false,
  style,
}: CollapsibleSectionHeaderProps) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: SPACE_SM + 2,
        padding: `${SPACE_MD}px ${SPACE_MD + 2}px`,
        fontSize: FONT_SIZE_BODY,
        background: active ? COLOR_ACTIVE_BG_SUBTLE : COLOR_SURFACE,
        borderRadius: RADIUS_CONTROL,
        cursor: "pointer",
        userSelect: "none",
        ...style,
      }}
    >
      {accentColor !== undefined && (
        <span
          style={{
            width: RADIUS_DOT * 2,
            height: RADIUS_DOT * 2,
            borderRadius: "50%",
            background: accentColor,
            flexShrink: 0,
          }}
        />
      )}
      <span
        style={{
          display: "flex",
          alignItems: "center",
          color: COLOR_TEXT_FAINT,
          flexShrink: 0,
        }}
      >
        {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
      </span>
      {leadingIcon && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: COLOR_TEXT_FAINT,
            flexShrink: 0,
          }}
        >
          {leadingIcon}
        </span>
      )}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          color: COLOR_TEXT_SECONDARY,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </span>
      {actions && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: SPACE_SM + 2,
            flexShrink: 0,
          }}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CollapsibleTreeRow — a tree-oriented sibling to CollapsibleSectionHeader.
//
// The distinguishing feature versus CollapsibleSectionHeader is click
// semantics: on tree rows, clicking the row body *selects* the item and only
// the chevron toggles expand/collapse. This matches how file managers and
// scene outliners behave (Finder, Blender Outliner, VS Code explorer).
//
// The primitive is usable for both collapsible rows (pass
// `isCollapsed` + `onToggleCollapse`) and leaf rows (omit both). That keeps
// HierarchyPanel's row frame — padding, selection highlight, trailing-action
// slot, indent — in one place instead of hand-coded per row type.
//
// Layout: [chevron?] [accentDot?] [leadingIcon?] [title ...] [actions?]
//
// Clicks inside `actions` are stopped from bubbling so delete / visibility
// buttons don't re-fire `onSelect`.
// ---------------------------------------------------------------------------

export interface CollapsibleTreeRowProps {
  /**
   * Title content. Pass a string for the default flex:1 ellipsis treatment,
   * or a ReactNode (e.g. a rename input) when the caller owns layout. Custom
   * ReactNodes should style themselves with `flex: 1; min-width: 0`.
   */
  title: ReactNode;
  /** Fires on row-body click (selection). Inert if omitted. */
  onSelect?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** Fires on right-click. */
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** "Active" highlight — this row represents the selected item. */
  selected?: boolean;
  /**
   * Optional double-click handler wired onto the title slot. Ignored when
   * `title` is a ReactNode (the caller is expected to wire it themselves).
   */
  onTitleDoubleClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  /** Optional forwarded ref to the row root (for focus / scroll-into-view). */
  rowRef?: RefObject<HTMLDivElement>;

  // Collapse: omit both to render a leaf row without a chevron.
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;

  /** Small colored dot before the chevron (section accent). */
  accentColor?: string;
  /** Leading icon slot (e.g. folder, mesh glyph). */
  leadingIcon?: ReactNode;
  /** Color applied to leadingIcon's container. Defaults to COLOR_TEXT_FAINT. */
  leadingIconColor?: string;
  /** Left padding offset for nested tree levels. Defaults to 0. */
  indent?: number;
  /** Trailing controls (badges, visibility, delete). Clicks do not bubble to onSelect. */
  actions?: ReactNode;
  /** Mute color — used for hidden / disabled items. Wins over `selected`. */
  dim?: boolean;
  /** Apply a more-dimmed base text color (COLOR_TEXT_DIM) for nested leaves. */
  muted?: boolean;
  /** Bump title font size by 1 (matches hierarchy-panel section scale). */
  emphasizeTitle?: boolean;
  /** Inline style overrides. */
  style?: CSSProperties;
}

export function CollapsibleTreeRow({
  title,
  onSelect,
  onContextMenu,
  selected = false,
  onTitleDoubleClick,
  rowRef,
  isCollapsed,
  onToggleCollapse,
  accentColor,
  leadingIcon,
  leadingIconColor,
  indent = 0,
  actions,
  dim = false,
  muted = false,
  emphasizeTitle = true,
  style,
}: CollapsibleTreeRowProps) {
  const collapsible = onToggleCollapse != null && isCollapsed !== undefined;

  const color = dim
    ? COLOR_TEXT_DISABLED
    : selected
      ? COLOR_TEXT
      : muted
        ? COLOR_TEXT_DIM
        : COLOR_TEXT_SECONDARY;

  return (
    <div
      ref={rowRef}
      onClick={onSelect}
      onContextMenu={onContextMenu}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 10px",
        paddingLeft: 10 + indent,
        borderRadius: RADIUS_CONTROL,
        cursor: onSelect ? "pointer" : "default",
        fontSize: FONT_SIZE_BODY + (emphasizeTitle ? 1 : 0),
        color,
        background: selected ? COLOR_ACTIVE_BG_SUBTLE : "transparent",
        ...style,
      }}
    >
      {collapsible && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse!();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: COLOR_TEXT_FAINT,
            width: 12,
            flexShrink: 0,
          }}
        >
          {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        </span>
      )}

      {accentColor !== undefined && (
        <span
          style={{
            width: RADIUS_DOT * 2,
            height: RADIUS_DOT * 2,
            borderRadius: "50%",
            background: accentColor,
            flexShrink: 0,
          }}
        />
      )}

      {leadingIcon && (
        <span
          style={{
            fontSize: FONT_SIZE_SMALL,
            color: dim
              ? COLOR_TEXT_DISABLED
              : (leadingIconColor ?? COLOR_TEXT_FAINT),
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          {leadingIcon}
        </span>
      )}

      {typeof title === "string" ? (
        <span
          onDoubleClick={onTitleDoubleClick}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </span>
      ) : (
        title
      )}

      {actions && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
          {actions}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CollapsiblePanel — a glass panel with a collapsible header. Built on top of
// FoldoutSection; the glass card wrapper and drag-handle wiring are the only
// additions over the primitive.
// ---------------------------------------------------------------------------

export function CollapsiblePanel({
  id,
  title,
  icon,
  summary,
  defaultCollapsed = false,
  children,
}: {
  /** Stable key for persisting collapsed state */
  id: string;
  /** Section title shown in the header */
  title: string;
  /** Icon displayed before the title */
  icon?: ReactNode;
  /** Compact content shown when collapsed */
  summary?: ReactNode;
  /** Whether the panel starts collapsed on first visit */
  defaultCollapsed?: boolean;
  children: ReactNode;
}) {
  return (
    <SidebarCard>
      <FoldoutSection
        id={id}
        label={title}
        icon={icon}
        summary={summary}
        defaultCollapsed={defaultCollapsed}
        isPanelHeader
      >
        {children}
      </FoldoutSection>
    </SidebarCard>
  );
}

// ---------------------------------------------------------------------------
// Divider — a thin horizontal rule between sections
// ---------------------------------------------------------------------------

export function Divider() {
  return (
    <div
      style={{
        borderTop: `0.5px solid ${COLOR_BORDER}`,
        margin: `${SPACE_MD}px 0`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Value display — consistent numeric readout next to sliders
// ---------------------------------------------------------------------------

/** Standard value display for full-width slider rows (e.g. transform, modifiers). */
export const SLIDER_VALUE_STYLE: CSSProperties = {
  fontSize: FONT_SIZE_BODY,
  color: COLOR_TEXT_SECONDARY,
  width: 48,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
};

/** Compact value display for dense property rows (e.g. material, curve, light). */
export const COMPACT_VALUE_STYLE: CSSProperties = {
  fontSize: FONT_SIZE_SMALL,
  color: COLOR_TEXT_SECONDARY,
  width: 36,
  textAlign: "right",
  fontVariantNumeric: "tabular-nums",
  flexShrink: 0,
};

// ---------------------------------------------------------------------------
// CompactRow — short-label + arbitrary content for dense property panels
// ---------------------------------------------------------------------------

export function CompactRow({
  label,
  labelWidth = 56,
  maxLabelWidth = 112,
  children,
}: {
  label: string;
  labelWidth?: number;
  maxLabelWidth?: number;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 6,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: FONT_SIZE_SMALL,
          color: COLOR_TEXT_DIM,
          width: "max-content",
          minWidth: labelWidth,
          maxWidth: `min(${maxLabelWidth}px, 45%)`,
          flex: "0 1 auto",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={label}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SliderRow — label + slider + formatted value
// ---------------------------------------------------------------------------

type SliderRowDensity = "default" | "compact";

export function SliderRow({
  label,
  value,
  min,
  max,
  step,
  stops,
  displayValue,
  onChange,
  density = "default",
  labelWidth,
  maxLabelWidth,
  /** Parse a user-typed string into a numeric value. Defaults to parseFloat. */
  parseValue,
  accent,
  defaultValue,
  midpoint,
  dimChromeOnDrag,
  onStart,
  onEnd,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  stops?: number[];
  displayValue: string;
  onChange: (v: number) => void;
  density?: SliderRowDensity;
  labelWidth?: number;
  maxLabelWidth?: number;
  parseValue?: (raw: string) => number;
  /** Accent color for the slider fill and thumb. */
  accent?: string;
  /** Default value — double-click / double-tap the thumb to reset. */
  defaultValue?: number;
  /** When set, this value maps to the visual centre (50%) of the track. */
  midpoint?: number;
  /** Opt in to mobile chrome dim while dragging — only for live-preview sliders. */
  dimChromeOnDrag?: boolean;
  /** Called once when the slider drag begins. */
  onStart?: () => void;
  /** Called once when the slider drag or typed commit ends. */
  onEnd?: () => void;
}) {
  const compact = density === "compact";
  const resolvedLabelWidth = labelWidth ?? (compact ? 56 : 90);
  const resolvedMaxLabelWidth = maxLabelWidth ?? (compact ? 112 : 150);
  const valueStyle = compact ? COMPACT_VALUE_STYLE : SLIDER_VALUE_STYLE;
  const resolvedStep = step ?? 1;
  const normalizeCommittedValue = (raw: number): number => {
    let next = raw;
    if (stops && stops.length > 0) {
      next = stops.reduce((best, stop) =>
        Math.abs(stop - raw) < Math.abs(best - raw) ? stop : best,
      );
    } else if (resolvedStep > 0) {
      next = Math.round(next / resolvedStep) * resolvedStep;
    }
    return Math.min(max, Math.max(min, next));
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 6,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: compact ? FONT_SIZE_SMALL : FONT_SIZE_BODY,
          color: COLOR_TEXT_DIM,
          width: "max-content",
          minWidth: resolvedLabelWidth,
          maxWidth: `min(${resolvedMaxLabelWidth}px, 45%)`,
          flex: "0 1 auto",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={label}
      >
        {label}
      </span>
      <Slider
        value={value}
        min={min}
        max={max}
        step={step}
        stops={stops}
        onChange={onChange}
        onStart={onStart}
        onEnd={onEnd}
        accent={accent}
        defaultValue={defaultValue}
        midpoint={midpoint}
        dimChromeOnDrag={dimChromeOnDrag}
      />
      <EditableValue
        displayValue={displayValue}
        valueStyle={valueStyle}
        fontSize={compact ? FONT_SIZE_SMALL : FONT_SIZE_BODY}
        onCommit={(raw) => {
          const parsed = parseValue ? parseValue(raw) : parseFloat(raw);
          if (!Number.isFinite(parsed)) return;
          onStart?.();
          onChange(normalizeCommittedValue(parsed));
          onEnd?.();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditableValue — tap the displayed value to type a precise number
// ---------------------------------------------------------------------------

function EditableValue({
  displayValue,
  valueStyle = SLIDER_VALUE_STYLE,
  fontSize = FONT_SIZE_BODY,
  onCommit,
}: {
  displayValue: string;
  valueStyle?: CSSProperties;
  fontSize?: number;
  onCommit: (raw: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = useCallback(() => {
    setEditing(true);
    // Focus after render
    requestAnimationFrame(() => inputRef.current?.select());
  }, []);

  const commit = useCallback(() => {
    const raw = inputRef.current?.value ?? "";
    setEditing(false);
    if (raw.trim()) onCommit(raw.trim());
  }, [onCommit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        setEditing(false);
      }
      // Block non-modifier keys from leaking to the global dispatcher,
      // but allow Cmd/Ctrl combos (e.g. Cmd+Z) to pass through.
      if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
    },
    [commit],
  );

  if (editing) {
    return (
      <input
        ref={inputRef}
        defaultValue={displayValue}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        style={{
          ...valueStyle,
          background: COLOR_SURFACE,
          border: `1px solid ${COLOR_ACCENT}`,
          borderRadius: RADIUS_SM,
          color: COLOR_TEXT_SECONDARY,
          padding: "1px 4px",
          outline: "none",
          fontSize,
          fontFamily: "inherit",
          fontVariantNumeric: "tabular-nums",
        }}
      />
    );
  }

  return (
    <span
      onClick={startEditing}
      style={{
        ...valueStyle,
        cursor: "pointer",
        borderRadius: RADIUS_SM,
        padding: "1px 4px",
      }}
      title="Click to edit"
    >
      {displayValue}
    </span>
  );
}

// ---------------------------------------------------------------------------
// LabeledRow — generic label + arbitrary content
// ---------------------------------------------------------------------------

export function LabeledRow({
  label,
  labelWidth = 60,
  children,
}: {
  label: string;
  labelWidth?: number;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 6,
      }}
    >
      <span
        style={{
          width: labelWidth,
          fontSize: FONT_SIZE_BODY,
          color: COLOR_TEXT_SUBTLE,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ToggleChips — a row of independently toggleable chips (multi-select)
// ---------------------------------------------------------------------------

export function ToggleChips({
  items,
}: {
  items: { label: string; active: boolean; onClick: () => void }[];
}) {
  return (
    <div style={{ display: "flex", flex: 1, gap: 4 }}>
      {items.map((item) => (
        <button
          key={item.label}
          onClick={item.onClick}
          style={{
            flex: 1,
            padding: "4px 10px",
            minHeight: 44,
            borderRadius: RADIUS_SM,
            fontSize: FONT_SIZE_SMALL,
            border: `0.5px solid ${item.active ? COLOR_ACCENT : COLOR_BORDER_STRONG}`,
            background: item.active ? COLOR_ACTIVE_BG_SUBTLE : "transparent",
            color: item.active ? COLOR_ACCENT : COLOR_TEXT_FAINT,
            cursor: "pointer",
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle — a switch-style boolean control
// ---------------------------------------------------------------------------

export function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div
      onClick={() => !disabled && onChange(!checked)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: 44,
        padding: "4px 0",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <span style={{ fontSize: FONT_SIZE_BODY, color: COLOR_TEXT_SECONDARY }}>
        {label}
      </span>
      <div
        style={{
          width: 32,
          height: 18,
          borderRadius: 9,
          background: checked ? COLOR_ACTIVE_BG : COLOR_BORDER_STRONG,
          position: "relative",
          transition: "background 0.15s",
        }}
      >
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: 7,
            background: checked ? COLOR_ACCENT : COLOR_TEXT_FAINT,
            position: "absolute",
            top: 2,
            left: checked ? 16 : 2,
            transition: "left 0.15s, background 0.15s",
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SegmentedControl — a row of mutually exclusive options
// ---------------------------------------------------------------------------

export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  disabled = false,
  vertical = false,
}: {
  options: { label: string; value: T }[];
  value: T | null;
  onChange: (v: T) => void;
  disabled?: boolean;
  /** Stack segments vertically instead of horizontally. */
  vertical?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        flex: vertical ? undefined : 1,
        borderRadius: RADIUS_SM,
        overflow: "hidden",
        border: `0.5px solid ${COLOR_BORDER_STRONG}`,
      }}
    >
      {options.map((opt, i) => {
        const active = value !== null && opt.value === value;
        return (
          <button
            key={String(opt.value)}
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            style={{
              flex: vertical ? undefined : 1,
              fontSize: FONT_SIZE_SMALL,
              padding: vertical ? "6px 4px" : "6px 0",
              minHeight: vertical ? undefined : 44,
              border: "none",
              borderLeft:
                !vertical && i > 0
                  ? `0.5px solid ${COLOR_BORDER_STRONG}`
                  : "none",
              borderTop:
                vertical && i > 0
                  ? `0.5px solid ${COLOR_BORDER_STRONG}`
                  : "none",
              background: active ? COLOR_ACTIVE_BG : COLOR_SURFACE,
              color: active ? COLOR_ACCENT : COLOR_TEXT_DIM,
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.55 : 1,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ButtonControlGroup — horizontal container for grouping icon buttons
// ---------------------------------------------------------------------------

/**
 * A purely compositional layout container for icon buttons.
 *
 * Unlike SegmentedControl, this imposes no mutual-exclusion semantics — each
 * child button remains fully independent.  Use it any time a row of icon
 * actions needs to share consistent spacing, and optionally a visible surface
 * container (e.g. a footer strip or a panel header action cluster).
 *
 * @example
 * // Bare (layout only):
 * <ButtonControlGroup>
 *   <button style={ICON_BUTTON_STYLE} onClick={onAdd}><Plus size={14}/></button>
 *   <button style={ICON_BUTTON_STYLE} onClick={onDelete}><Minus size={14}/></button>
 * </ButtonControlGroup>
 *
 * @example
 * // Bordered (visible container):
 * <ButtonControlGroup bordered>
 *   <button style={ICON_BUTTON_STYLE} onClick={onMoveUp}><ChevronUp size={14}/></button>
 *   <button style={ICON_BUTTON_STYLE} onClick={onMoveDown}><ChevronDown size={14}/></button>
 * </ButtonControlGroup>
 */
export function ButtonControlGroup({
  children,
  bordered = false,
  gap = SPACE_MD,
}: {
  children: ReactNode;
  /** Wrap in a visible surface container (background + border). Default: false. */
  bordered?: boolean;
  /** Gap between children in pixels. Default: SPACE_MD (8). */
  gap?: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap,
        ...(bordered
          ? {
              background: COLOR_SURFACE,
              border: `0.5px solid ${COLOR_BORDER_STRONG}`,
              borderRadius: RADIUS_SM,
              padding: "2px 4px",
            }
          : undefined),
      }}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SidebarButton — compact button used in the ObjectSidebar
// ---------------------------------------------------------------------------

export function SidebarButton({
  icon,
  label,
  active,
  onClick,
  shortcut,
  danger,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      title={shortcut ? `${label} (${shortcut})` : label}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        padding: "6px 10px",
        borderRadius: RADIUS_CONTROL,
        border: "none",
        background: active
          ? COLOR_ACTIVE_BG
          : danger
            ? COLOR_DANGER_BG
            : COLOR_SURFACE,
        color: disabled
          ? COLOR_TEXT_DISABLED
          : active
            ? COLOR_ACCENT
            : danger
              ? COLOR_DANGER
              : COLOR_TEXT_SUBTLE,
        cursor: disabled ? "default" : "pointer",
        fontSize: FONT_SIZE_SMALL,
        opacity: disabled ? 0.5 : 1,
        width: "100%",
      }}
    >
      {icon}
      <span>{label}</span>
      {shortcut && <Kbd shortcut={shortcut} />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Kbd — keyboard shortcut chip
// ---------------------------------------------------------------------------

export function Kbd({ shortcut }: { shortcut: string }) {
  return (
    <kbd
      style={{
        display: "inline-block",
        padding: "1px 5px",
        borderRadius: RADIUS_XS,
        fontSize: FONT_SIZE_TINY,
        lineHeight: "16px",
        fontFamily: "inherit",
        background: COLOR_BORDER,
        color: COLOR_TEXT_DIM,
        border: `1px solid ${COLOR_BORDER}`,
        marginLeft: "auto",
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {shortcut}
    </kbd>
  );
}

// ---------------------------------------------------------------------------
// Modifier helpers — shared by ObjectSidebar and any future modifier UI
// ---------------------------------------------------------------------------

export function modKindLabel(kind: BlendModifier["kind"]): string {
  switch (kind) {
    case "mirror":
      return "Mirror";
    case "subsurf":
      return "Subdivision";
    case "solidify":
      return "Solidify";
    case "array":
      return "Array";
    case "skin":
      return "Skin";
    case "instance":
      return "Instance";
    case "shrinkwrap":
      return "Shrink Wrap";
    case "curve":
      return "Curve";
    case "followObject":
      return "Follow Object";
    case "noise":
      return "Noise";
    case "boolean":
      return "Boolean";
    case "smooth":
      return "Smooth";
    case "bevel":
      return "Bevel";
    default:
      return kind;
  }
}

export function modKindColor(kind: BlendModifier["kind"]): string {
  switch (kind) {
    case "mirror":
      return COLOR_ACCENT;
    case "subsurf":
      return COLOR_MOD_SUBSURF;
    case "solidify":
      return COLOR_MOD_SOLIDIFY;
    case "array":
      return COLOR_MOD_ARRAY;
    case "skin":
      return COLOR_MOD_SKIN;
    case "instance":
      return COLOR_MOD_INSTANCE;
    case "shrinkwrap":
      return COLOR_MOD_SHRINKWRAP;
    case "curve":
      return COLOR_MOD_CURVE;
    case "followObject":
      return COLOR_MOD_FOLLOW;
    case "noise":
      return COLOR_MOD_NOISE;
    case "boolean":
      return COLOR_MOD_BOOLEAN;
    case "smooth":
      return COLOR_MOD_SMOOTH;
    case "bevel":
      return COLOR_MOD_BEVEL;
    default:
      return COLOR_TEXT_DIM;
  }
}

// ---------------------------------------------------------------------------
// Menu primitives — re-exported from menu/ for consumers that import from shared
// ---------------------------------------------------------------------------

export {
  MenuItem as PopoverMenuItem,
  renderLabelWithShortcut,
} from "./menu/MenuItem.tsx";
export { MenuDivider as PopoverDivider } from "./menu/MenuPrimitives.tsx";

// ---------------------------------------------------------------------------
// Select — generic labeled dropdown picker (text-based, Popover-backed)
// ---------------------------------------------------------------------------

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  /** Optional leading graphic (e.g. a color swatch) rendered before the label in both the trigger and list rows. */
  icon?: ReactNode;
}

/**
 * Compact labeled dropdown. Shows current selection as text with a chevron;
 * opens a Popover with a "None" option (to clear) plus all provided options.
 *
 * @example
 * <Select
 *   label="Offset Object"
 *   value={mod.offsetObjectName ?? null}
 *   options={[{ value: "Empty", label: "Empty" }]}
 *   onChange={(v) => setArrayOffsetObject(name, idx, v)}
 * />
 */
export function Select<T extends string = string>({
  label,
  value,
  options,
  onChange,
  placeholder = "None",
  eyedropperPick,
}: {
  label: string;
  value: T | null;
  options: SelectOption<T>[];
  onChange: (value: T | null) => void;
  placeholder?: string;
  /**
   * When provided, renders an eyedropper icon button that lets the user pick
   * a value directly from the viewport. The integration calls
   * `beginEyedropperSession` here and forwards the callbacks.
   *
   * @example
   * eyedropperPick={(onPick, onCancel) => {
   *   useStore.getState().beginEyedropperSession(
   *     (id) => onPick(id as T),
   *     onCancel,
   *   );
   * }}
   */
  eyedropperPick?: (onPick: (value: T) => void, onCancel: () => void) => void;
}) {
  const [open, setOpen] = useState(false);
  const [isPicking, setIsPicking] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const selected = options.find((o) => o.value === value);

  function startEyedropper() {
    setOpen(false);
    setIsPicking(true);
    eyedropperPick!(
      (v) => {
        onChange(v);
        setIsPicking(false);
      },
      () => {
        setIsPicking(false);
      },
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 10px",
      }}
    >
      <span
        style={{
          fontSize: FONT_SIZE_BODY,
          color: COLOR_TEXT_MUTED,
          width: 90,
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <button
        ref={anchorRef}
        onClick={() => setOpen((v) => !v)}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 4,
          padding: "3px 8px",
          background: "transparent",
          border: `1px solid ${COLOR_BORDER}`,
          borderRadius: RADIUS_CONTROL,
          color: selected ? COLOR_TEXT_SECONDARY : COLOR_TEXT_DIM,
          fontSize: FONT_SIZE_BODY,
          cursor: "pointer",
          minWidth: 0,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={12} style={{ flexShrink: 0, opacity: 0.6 }} />
      </button>
      {eyedropperPick && (
        <button
          title="Pick from viewport"
          onClick={startEyedropper}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 4,
            background: "transparent",
            border: "none",
            borderRadius: RADIUS_CONTROL,
            color: isPicking ? COLOR_ACCENT : COLOR_TEXT_MUTED,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Pipette size={12} />
        </button>
      )}
      <Popover
        anchorRef={anchorRef as RefObject<HTMLElement | null>}
        open={open}
        onClose={() => setOpen(false)}
        style={{ minWidth: 140 }}
      >
        <button
          data-popover-item
          onClick={() => {
            onChange(null);
            setOpen(false);
          }}
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            padding: "6px 12px",
            border: "none",
            background: "transparent",
            color: value === null ? COLOR_ACCENT : COLOR_TEXT_SECONDARY,
            fontSize: FONT_SIZE_BODY,
            textAlign: "left",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {placeholder}
        </button>
        {options.length > 0 && (
          <div
            style={{ height: 1, background: COLOR_BORDER, margin: "2px 0" }}
          />
        )}
        {options.map((opt) => (
          <button
            key={opt.value}
            data-popover-item
            onClick={() => {
              onChange(opt.value);
              setOpen(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "6px 12px",
              border: "none",
              background: "transparent",
              color: opt.value === value ? COLOR_ACCENT : COLOR_TEXT_SECONDARY,
              fontSize: FONT_SIZE_BODY,
              textAlign: "left",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {opt.label}
          </button>
        ))}
      </Popover>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SelectSearch — searchable labeled dropdown (drop-in upgrade of Select)
// ---------------------------------------------------------------------------

/**
 * Identical to `Select` but adds a live-filter search field inside the
 * popover.  Best used when the option list is dynamic or long (e.g. an object
 * picker or material picker).
 *
 * The `label` prop is optional: when omitted the trigger fills its container
 * directly (no 90 px label column), making it suitable for standalone use
 * inside custom rows.
 *
 * @example
 * // Labeled row (mirrors Select usage):
 * <SelectSearch label="Object" value={name} options={sourceOptions} onChange={set} />
 *
 * @example
 * // Standalone (no label column):
 * <SelectSearch value={matId} options={matOptions} onChange={onAssign} />
 */
export function SelectSearch<T extends string = string>({
  label,
  value,
  options,
  onChange,
  placeholder = "None",
  searchPlaceholder = "Search…",
  allowClear = true,
  eyedropperPick,
}: {
  label?: string;
  value: T | null;
  options: SelectOption<T>[];
  onChange: (value: T | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Show a "None" clear option at the top of the list. Default: true. */
  allowClear?: boolean;
  /**
   * When provided, renders an eyedropper icon button that lets the user pick
   * a value directly from the viewport. The integration calls
   * `beginEyedropperSession` here and forwards the callbacks.
   *
   * @example
   * eyedropperPick={(onPick, onCancel) => {
   *   useStore.getState().beginEyedropperSession(
   *     (id) => onPick(id as T),
   *     onCancel,
   *   );
   * }}
   */
  eyedropperPick?: (onPick: (value: T) => void, onCancel: () => void) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isPicking, setIsPicking] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  useIsMobile();
  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  function startEyedropper() {
    handleClose();
    setIsPicking(true);
    eyedropperPick!(
      (v) => {
        onChange(v);
        setIsPicking(false);
      },
      () => {
        setIsPicking(false);
      },
    );
  }

  const eyedropperButton = eyedropperPick ? (
    <button
      title="Pick from viewport"
      onClick={startEyedropper}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 4,
        background: "transparent",
        border: "none",
        borderRadius: RADIUS_CONTROL,
        color: isPicking ? COLOR_ACCENT : COLOR_TEXT_MUTED,
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <Pipette size={12} />
    </button>
  ) : null;

  const trigger = (
    <button
      ref={anchorRef}
      onClick={() => setOpen((v) => !v)}
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 4,
        padding: "3px 8px",
        background: "transparent",
        border: `1px solid ${COLOR_BORDER}`,
        borderRadius: RADIUS_CONTROL,
        color: selected ? COLOR_TEXT_SECONDARY : COLOR_TEXT_DIM,
        fontSize: FONT_SIZE_BODY,
        cursor: "pointer",
        minWidth: 0,
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {selected?.icon}
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {selected ? selected.label : placeholder}
        </span>
      </span>
      <ChevronDown size={12} style={{ flexShrink: 0, opacity: 0.6 }} />
    </button>
  );

  const popover = (
    <Popover
      anchorRef={anchorRef as RefObject<HTMLElement | null>}
      open={open}
      onClose={handleClose}
      style={{ minWidth: 160, padding: 0 }}
    >
      {/* Search field */}
      <div
        style={{
          borderBottom: `1px solid ${COLOR_BORDER}`,
          padding: "4px 8px",
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            outline: "none",
            color: COLOR_TEXT,
            fontSize: FONT_SIZE_SMALL,
            fontFamily: "inherit",
          }}
        />
      </div>

      {/* Option list */}
      <div style={{ maxHeight: 220, overflowY: "auto" }}>
        {/* None / clear option */}
        {allowClear && (
          <button
            data-popover-item
            onClick={() => {
              onChange(null);
              handleClose();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "6px 12px",
              border: "none",
              background: "transparent",
              color: value === null ? COLOR_ACCENT : COLOR_TEXT_SECONDARY,
              fontSize: FONT_SIZE_BODY,
              textAlign: "left",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {placeholder}
          </button>
        )}

        {allowClear && filtered.length > 0 && (
          <div
            style={{ height: 1, background: COLOR_BORDER, margin: "2px 0" }}
          />
        )}

        {filtered.map((opt) => (
          <button
            key={opt.value}
            data-popover-item
            onClick={() => {
              onChange(opt.value);
              handleClose();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: opt.icon ? 6 : 0,
              width: "100%",
              padding: "6px 12px",
              border: "none",
              background: "transparent",
              color: opt.value === value ? COLOR_ACCENT : COLOR_TEXT_SECONDARY,
              fontSize: FONT_SIZE_BODY,
              textAlign: "left",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {opt.icon}
            {opt.label}
          </button>
        ))}

        {filtered.length === 0 && search && (
          <div
            style={{
              padding: "8px 12px",
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_FAINT,
              textAlign: "center",
            }}
          >
            No results
          </div>
        )}
      </div>
    </Popover>
  );

  if (label) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 10px",
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_BODY,
            color: COLOR_TEXT_MUTED,
            width: 90,
            flexShrink: 0,
          }}
        >
          {label}
        </span>
        {trigger}
        {eyedropperButton}
        {popover}
      </div>
    );
  }

  return (
    <div
      style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}
    >
      {trigger}
      {eyedropperButton}
      {popover}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ListControl — selectable vertical item list with integrated footer actions
// ---------------------------------------------------------------------------

/** Minimum shape for items rendered by ListControl. */
export interface ListControlItem {
  id: string;
  label: string;
  /** Optional content rendered before the label (e.g. a role icon). Hidden while renaming. */
  leading?: ReactNode;
  /** Optional content rendered after the label (e.g. a count badge). Hidden while renaming. */
  trailing?: ReactNode;
}

const LIST_FOOTER_BTN: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  padding: 0,
  borderRadius: RADIUS_SM,
  border: "none",
  background: "transparent",
  color: COLOR_TEXT_SUBTLE,
  cursor: "pointer",
  flexShrink: 0,
};

// Internal label component — swaps between display span and rename input.
function ListControlItemLabel({
  id,
  label,
  isActive,
  onRename,
  leading,
  trailing,
}: {
  id: string;
  label: string;
  isActive: boolean;
  onRename?: (id: string, name: string) => void;
  leading?: ReactNode;
  trailing?: ReactNode;
}) {
  const { isRenaming, startRenaming, inputProps } = useInlineRename(
    label,
    (next) => onRename?.(id, next),
  );

  if (isRenaming) {
    return (
      <input
        {...inputProps}
        type="text"
        style={{
          flex: 1,
          padding: "1px 4px",
          borderRadius: RADIUS_SM,
          border: `1px solid ${COLOR_ACCENT}`,
          background: COLOR_SURFACE,
          color: COLOR_TEXT,
          fontSize: FONT_SIZE_SMALL,
          fontFamily: "inherit",
          outline: "none",
          minWidth: 0,
        }}
      />
    );
  }

  return (
    <>
      {leading && (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: isActive ? COLOR_ACCENT : COLOR_TEXT_FAINT,
            flexShrink: 0,
            marginRight: 6,
          }}
        >
          {leading}
        </span>
      )}
      <span
        onDoubleClick={onRename ? (e) => startRenaming(e) : undefined}
        title={onRename ? "Double-click to rename" : undefined}
        style={{
          flex: 1,
          fontSize: FONT_SIZE_SMALL,
          color: isActive ? COLOR_ACCENT : COLOR_TEXT_DIM,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {label}
      </span>
      {trailing}
      {onRename && (
        <button
          onClick={(e) => startRenaming(e)}
          title="Rename"
          style={{
            ...LIST_FOOTER_BTN,
            width: 20,
            height: 20,
            opacity: 0.6,
          }}
        >
          <Pencil size={11} />
        </button>
      )}
    </>
  );
}

/**
 * A Blender-style vertical item list with single-row selection and an
 * integrated footer control strip.
 *
 * The list area is scrollable; the footer houses Add / Delete buttons
 * (and any extra `footerActions`) that operate on the active item.
 *
 * @example
 * <ListControl
 *   items={materialSlots}
 *   activeId={activeSlotId}
 *   onSelect={setActiveSlot}
 *   onAdd={addSlot}
 *   onDelete={deleteSlot}
 * />
 */
export function ListControl<T extends ListControlItem>({
  items,
  activeId,
  onSelect,
  onAdd,
  onDelete,
  onRename,
  canDelete,
  footerActions,
  emptyMessage = "No items",
  maxHeight = 120,
}: {
  items: T[];
  activeId: string | null;
  onSelect: (id: string) => void;
  /** Renders a + button in the footer. */
  onAdd?: () => void;
  /** Renders a − button in the footer; disabled when no active item or canDelete returns false. */
  onDelete?: (id: string) => void;
  /** Enables double-click and pencil-icon inline rename for each row. */
  onRename?: (id: string, name: string) => void;
  /** Optional guard that controls whether the Delete button is enabled. */
  canDelete?: (items: T[], activeItem: T | null) => boolean;
  /** Additional ReactNode rendered after the built-in Add/Delete buttons. */
  footerActions?: ReactNode;
  /** Shown when items is empty. Default: "No items". */
  emptyMessage?: string;
  /** Max height of the scrollable list area in pixels. Default: 120. */
  maxHeight?: number;
}) {
  const activeItem = items.find((i) => i.id === activeId) ?? null;
  const deleteEnabled =
    !!onDelete &&
    !!activeItem &&
    (canDelete ? canDelete(items, activeItem) : true);

  const hasFooter = onAdd || onDelete || footerActions;

  return (
    <div
      style={{
        border: `0.5px solid ${COLOR_BORDER_STRONG}`,
        borderRadius: RADIUS_SM,
        overflow: "hidden",
      }}
    >
      {/* Scrollable list */}
      <div style={{ overflowY: "auto", maxHeight }}>
        {items.length === 0 ? (
          <div
            style={{
              padding: "8px 10px",
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_FAINT,
            }}
          >
            {emptyMessage}
          </div>
        ) : (
          items.map((item, idx) => {
            const isActive = item.id === activeId;
            return (
              <div
                key={item.id}
                onClick={() => onSelect(item.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 8px",
                  minHeight: 28,
                  cursor: "pointer",
                  background: isActive ? COLOR_ACTIVE_BG : "transparent",
                  borderTop: idx > 0 ? `0.5px solid ${COLOR_BORDER}` : "none",
                }}
              >
                <ListControlItemLabel
                  id={item.id}
                  label={item.label}
                  isActive={isActive}
                  onRename={onRename}
                  leading={item.leading}
                  trailing={item.trailing}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Footer control strip */}
      {hasFooter && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: SPACE_SM,
            padding: "2px 4px",
            borderTop: `0.5px solid ${COLOR_BORDER_STRONG}`,
            background: COLOR_SURFACE,
          }}
        >
          {onAdd && (
            <button onClick={onAdd} title="Add" style={LIST_FOOTER_BTN}>
              <Plus size={12} />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => activeItem && onDelete(activeItem.id)}
              disabled={!deleteEnabled}
              title="Delete"
              style={{
                ...LIST_FOOTER_BTN,
                opacity: deleteEnabled ? 1 : 0.35,
                cursor: deleteEnabled ? "pointer" : "default",
              }}
            >
              <Minus size={12} />
            </button>
          )}
          {footerActions}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ShapeKeyList — shared shape key slider list used by HierarchyPanel & Sidebar
// ---------------------------------------------------------------------------

export function ShapeKeyList({
  setName,
  keys,
  filter = "",
}: {
  setName: string;
  keys: {
    name: string;
    index: number;
    min: number;
    max: number;
    defaultValue: number;
  }[];
  filter?: string;
}) {
  const driverGraph = useStore((s) => s.driverGraph);
  const drivenKeys = useMemo(
    () => driverGraph?.getDrivenKeysForSet(setName) ?? new Set<string>(),
    [driverGraph, setName],
  );

  return (
    <>
      {keys
        .filter((k) => k.index > 0)
        .filter((k) => !drivenKeys.has(k.name))
        .filter((k) => !filter || k.name.toLowerCase().includes(filter))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((key) => (
          <ShapeKeyRow
            key={key.name}
            setName={setName}
            keyName={key.name}
            min={key.min}
            max={key.max}
            defaultValue={key.defaultValue}
          />
        ))}
    </>
  );
}

/**
 * Individual shape key row. Subscribes to its own weight via a precise
 * selector so that changing one key doesn't re-render all the others.
 */
function ShapeKeyRow({
  setName,
  keyName,
  min,
  max,
  defaultValue,
}: {
  setName: string;
  keyName: string;
  min: number;
  max: number;
  defaultValue: number;
}) {
  const weight = useStore(
    (s) => s.shapeKeyWeights.get(setName)?.get(keyName) ?? 0,
  );
  const isFav = useStore((s) =>
    s.favoriteShapeKeys.has(`${setName}::${keyName}`),
  );
  const setWeight = useStore((s) => s.setShapeKeyWeight);
  const toggleFav = useStore((s) => s.toggleFavoriteShapeKey);
  const undo = useDeferredUndo();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        fontSize: FONT_SIZE_BODY + 1,
        minWidth: 0,
      }}
    >
      <span
        onClick={() => toggleFav(setName, keyName)}
        style={{
          cursor: "pointer",
          flexShrink: 0,
          color: isFav ? COLOR_GOLD : COLOR_TEXT_DISABLED,
          display: "flex",
          alignItems: "center",
        }}
      >
        {isFav ? <StarFilledIcon /> : <StarIcon />}
      </span>
      <span
        style={{
          width: 90,
          flexShrink: 0,
          color: COLOR_TEXT_MUTED,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: FONT_SIZE_BODY,
        }}
      >
        {keyName}
      </span>
      <Slider
        min={min * 100}
        max={max * 100}
        value={weight * 100}
        defaultValue={defaultValue * 100}
        onStart={undo.pushOnce}
        onEnd={undo.reset}
        onChange={(v) => setWeight(setName, keyName, v / 100)}
      />
      <EditableValue
        displayValue={weight.toFixed(2)}
        onCommit={(raw) => {
          const parsed = parseFloat(raw);
          if (Number.isFinite(parsed)) {
            undo.pushOnce();
            setWeight(setName, keyName, Math.min(max, Math.max(min, parsed)));
            undo.reset();
          }
        }}
      />
    </div>
  );
}
